import { z } from "zod";
import type { ActionCue } from "./storyActions";

/** New streamed content is silent unless the caller explicitly selects a voice mode. */
export const zStoryAudioDelivery = z.enum(["off", "device", "wait", "async"]);
export type StoryAudioDelivery = z.infer<typeof zStoryAudioDelivery>;

export const SPEECH_MODELS = ["gemini-3.8-flash-tts", "gemini-3.8-flash-lite-tts"] as const;
export const zSpeechModel = z.enum(SPEECH_MODELS);
export type SpeechModel = z.infer<typeof zSpeechModel>;

export const DEFAULT_CHARACTER_VOICES = [
  { id: "Puck", name: "Puck", description: "Upbeat and playful" },
  { id: "Kore", name: "Kore", description: "Clear and confident" },
  { id: "Lumi", name: "Lumi", description: "Lumi voice" },
  { id: "Aoede", name: "Aoede", description: "Breezy and friendly" },
  { id: "Charon", name: "Charon", description: "Calm and informative" },
  { id: "Orus", name: "Orus", description: "Firm and reassuring" },
  { id: "Sulafat", name: "Sulafat", description: "Warm and gentle" },
] as const;

export const DR_RABBIT_VOICE_DESCRIPTION =
  "An upbeat, energetic, and warm adult male voice portraying a friendly cartoon mascot for a children's educational show. Speak in a bright tenor pitch with a smiling, encouraging, and enthusiastic tone. Keep articulation crisp and clear, with dynamic, playful inflections and a lively, motivating cadence suitable for guiding children through a fun routine.";

export const zSpeechAudioAsset = z.object({
  path: z.string().min(1).max(1000),
  url: z.string().url().max(3000),
  durationMs: z.number().finite().positive(),
  mimeType: z.literal("audio/wav"),
});
export type SpeechAudioAsset = z.infer<typeof zSpeechAudioAsset>;

export const zCharacterVoiceProfile = z.object({
  kind: z.enum(["prebuilt", "designed"]),
  voiceId: z.string().min(1).max(200).optional(),
  voiceName: z.string().min(1).max(100).optional(),
  description: z.string().max(6000),
  model: zSpeechModel.default("gemini-3.8-flash-tts"),
  languageCode: z.string().min(2).max(30).optional(),
  preview: zSpeechAudioAsset.optional(),
});
export type CharacterVoiceProfile = z.infer<typeof zCharacterVoiceProfile>;

/** A story's delivery language overrides a reusable actor voice without changing its identity. */
export function storyDeliveryVoiceProfile(profile: CharacterVoiceProfile, language?: string): CharacterVoiceProfile {
  return language && /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(language) ? { ...profile, languageCode: language } : profile;
}

export const zPerformanceSpeechLine = zSpeechAudioAsset.extend({
  lineId: z.string().min(1).max(220),
  beatId: z.string().min(1).max(100),
  actionIndex: z.number().int().nonnegative(),
  characterId: z.string().min(1).max(100),
  text: z.string().max(16000),
  style: z.string().max(6000),
  sourceKey: z.string().regex(/^[a-f0-9]{64}$/),
  voiceKey: z.string().regex(/^[a-f0-9]{64}$/),
  voiceSource: z.string().max(7000).optional(),
  model: zSpeechModel,
  generatedAt: z.number().finite().nonnegative(),
});
export type PerformanceSpeechLine = z.infer<typeof zPerformanceSpeechLine>;
export const zPerformanceSpeech = z.object({ version: z.literal(1), lines: z.record(zPerformanceSpeechLine) });
export type PerformanceSpeech = z.infer<typeof zPerformanceSpeech>;

/** Small breathing room at 1x; a new speaker gets time to receive the last line. */
export function speechTurnGapMs(characterId: string, nextCharacterId?: string): number {
  return nextCharacterId && nextCharacterId !== characterId ? 360 : 180;
}

/** Recorded speech owns its cue's duration; intentional silence belongs to the beat hold. */
export function speechPlaybackDurationMs(durationMs: number, characterId: string, nextCharacterId?: string): number {
  return Math.max(0, Number.isFinite(durationMs) ? durationMs : 0) + speechTurnGapMs(characterId, nextCharacterId);
}

export function speechLineKey(beatId: string, actionIndex: number): string {
  return `${beatId}:${actionIndex}`;
}
export const speechLineId = speechLineKey;

/** The exact transcript and per-turn direction; stage timing does not invalidate speech. */
export function speechLineSource(beat: ActionCue, index?: number) {
  const actionIndex = index ?? beat.actions.findIndex((action) => action.kind === "say" || action.kind === "narrate");
  const action = beat.actions[actionIndex];
  if (!action || (action.kind !== "say" && action.kind !== "narrate") || !action.text.trim()) return undefined;
  return {
    lineId: speechLineKey(beat.id, actionIndex),
    beatId: beat.id,
    actionIndex,
    characterId: action.kind === "say" ? action.character || "narrator" : "narrator",
    text: action.text,
    style: action.kind === "say" ? action.hint || "" : "",
  };
}

export function speechVoiceSource(profile: CharacterVoiceProfile): string {
  return JSON.stringify([
    profile.kind,
    profile.kind === "designed" ? profile.voiceId : profile.voiceName || profile.voiceId,
    profile.description,
    profile.languageCode || "en-US",
    profile.model,
  ]);
}

export function isSpeechLineCurrent(
  line: PerformanceSpeechLine,
  beat: ActionCue,
  profile?: CharacterVoiceProfile
): boolean {
  const source = speechLineSource(beat, line.actionIndex);
  return (
    !!source &&
    source.lineId === line.lineId &&
    source.characterId === line.characterId &&
    source.text === line.text &&
    source.style === line.style &&
    (!profile || !line.voiceSource || line.voiceSource === speechVoiceSource(profile))
  );
}

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
// Older custom-voice clients sent voiceId: undefined, which Firebase encodes as null.
// Accept only that absent design identity at the request boundary; stored profiles stay strict.
const zVoiceRequestProfile = z.preprocess((input) => {
  if (
    input &&
    typeof input === "object" &&
    !Array.isArray(input) &&
    "kind" in input &&
    input.kind === "designed" &&
    "voiceId" in input &&
    input.voiceId === null
  ) {
    const { voiceId: _absent, ...profile } = input;
    return profile;
  }
  return input;
}, zCharacterVoiceProfile);
export const zManageSpeechRequest = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("voice"),
    operation: z.enum(["suggest", "save", "design", "preview", "remove"]),
    accountId: id,
    characterId: id,
    kind: z.enum(["character", "mind"]).optional(),
    personaId: id.optional(),
    profile: zVoiceRequestProfile.optional(),
    text: z.string().min(1).max(2000).optional(),
  }),
  z.object({
    action: z.literal("scene"),
    operation: z.enum(["generate", "status"]),
    accountId: id,
    performanceId: id,
    sceneId: id,
    beatId: id.optional(),
    actionIndex: z.number().int().nonnegative().optional(),
    model: zSpeechModel.optional(),
    characterIds: z.array(id).max(100).optional(),
    force: z.boolean().optional(),
  }),
]);
export type ManageSpeechRequest = z.infer<typeof zManageSpeechRequest>;
export interface ManageSpeechResponse {
  profile?: CharacterVoiceProfile;
  audio?: SpeechAudioAsset;
  speech?: PerformanceSpeech;
  generated?: number;
  cached?: number;
  skipped?: number;
  errors?: Array<{ lineId: string; message: string }>;
  editorVersion?: string;
}
